
import { VcStorage } from './vcstorage';

export var VcLocationsStorage = (function () {
    const STORAGE_KEY = 'vcstate';
    const MAX_LOCATIONS = 10;
    const DEFAULT_STATE = {
        locations: [],
        unitGroup: 'metric',
        cid: null,
        required: {
            necessary: true,
            functional: false,
            analysis: false,
            ads: false
        }
    };

    var storage = window && window.localStorage;
    var vcstate = JSON.parse(JSON.stringify(DEFAULT_STATE));

    var processCookieValues = function () {
        window.zE && window.zE('webWidget', 'updateSettings', {
            cookies: hasFunctionalCookies()
        });
        if (window.gtag) {
            gtag('consent', 'update', {
                'ad_storage': hasAdsCookies() ? 'granted' : 'denied',
                'analytics_storage': hasAnalysisCookies() ? 'granted' : 'denied'
            });
        }
    };

    // On native apps or standalone pages (no cookie banner), always allow full storage.
    // Cookie consent only matters on the main website where the banner is shown.
    var _isCookieConsentRequired = function () {
        // Native Capacitor app — no browser cookies involved
        if (VcStorage.isNative()) return false;
        // Only enforce consent if the user has previously interacted with the cookie banner
        // (hasSetCookies is set by enableAllCookies / disableAllCookies / setAllCookies).
        // On standalone pages where no banner exists, hasSetCookies is never set,
        // so storage is unrestricted.
        if (!vcstate || !vcstate.required || !vcstate.required.hasSetCookies) return false;
        return true;
    };

    var _saveState = function () {
        var state;
        if (!_isCookieConsentRequired() || hasFunctionalCookies()) {
            state = vcstate;
        } else {
            state = { required: vcstate.required };
            // Always persist authentication tokens (necessary for security, not tracking)
            if (vcstate.vc2FADeviceToken) {
                state.vc2FADeviceToken = vcstate.vc2FADeviceToken;
            }
        }
        var json = JSON.stringify(state);

        // Sync write to localStorage (fast, keeps existing web behavior)
        if (storage) {
            try {
                storage.setItem(STORAGE_KEY, json);
            } catch (e) {
                console.log('Error storing state to localStorage');
            }
        }
        // Async write to Capacitor Preferences (reliable on native)
        VcStorage.set(STORAGE_KEY, json).catch(function (e) {
            console.log('Error storing state to VcStorage');
        });

        processCookieValues();
    };

    // Synchronous restore from localStorage (immediate on startup)
    var _restoreStateSync = function () {
        if (storage) {
            try {
                var raw = storage.getItem(STORAGE_KEY);
                if (raw) vcstate = JSON.parse(raw);
            } catch (e) {
                // ignore parse errors
            }
        }
        _ensureDefaults();
    };

    // Async restore from Capacitor Preferences (overrides if available)
    var _restoreStateAsync = function () {
        return VcStorage.get(STORAGE_KEY).then(function (raw) {
            if (raw) {
                try {
                    var parsed = JSON.parse(raw);
                    // Merge: native state takes precedence
                    vcstate = parsed;
                    _ensureDefaults();
                    // Sync back to localStorage so sync reads are up-to-date
                    if (storage) {
                        try { storage.setItem(STORAGE_KEY, raw); } catch (e) { /* ignore */ }
                    }
                } catch (e) {
                    // ignore parse errors
                }
            }
        }).catch(function () {
            // VcStorage not available, keep localStorage state
        });
    };

    var _ensureDefaults = function () {
        if (!vcstate.required) {
            vcstate.required = JSON.parse(JSON.stringify(DEFAULT_STATE.required));
        }
        if (!vcstate.locations) {
            vcstate.locations = [];
        }
        if (!vcstate.cid) {
            vcstate.cid = Math.floor(Math.random() * Date.now()).toString(16);
            _saveState();
        }
        processCookieValues();
    };

    var _addLocation = function (newLocation) {
        if (!newLocation || !newLocation.location) return;

        var locations = vcstate.locations || [];
        locations.unshift(newLocation);

        var dups = {};
        locations = locations.filter(function (d) {
            if (!d.location || dups[d.location]) return false;
            dups[d.location] = true;
            return true;
        });

        if (locations.length > MAX_LOCATIONS) locations = locations.slice(0, MAX_LOCATIONS);
        vcstate.locations = locations;
        _saveState();
    };

    var _removeLocation = function (locationOrIndex) {
        var locations = vcstate.locations || [];
        if (Number.isInteger(locationOrIndex)) {
            locations.splice(locationOrIndex, 1);
        } else if (locationOrIndex && locationOrIndex.location) {
            var target = locationOrIndex.location;
            locations = locations.filter(function (d) {
                return d.location !== target;
            });
        } else if (typeof locationOrIndex === 'string') {
            locations = locations.filter(function (d) {
                return d.location !== locationOrIndex;
            });
        }

        vcstate.locations = locations;
        _saveState();
    };

    var hasFunctionalCookies = function () {
        return vcstate && vcstate.required && vcstate.required.functional;
    };
    var hasAnalysisCookies = function () {
        return vcstate && vcstate.required && vcstate.required.analysis;
    };
    var hasAdsCookies = function () {
        return vcstate && vcstate.required && vcstate.required.ads;
    };

    var _setUnitGroup = function (unitGroup) {
        vcstate.unitGroup = unitGroup;
        _saveState();
    };
    var _getUnitGroup = function () {
        return vcstate.unitGroup;
    };

    var _setValue = function (name, value, required) {
        if (required) {
            vcstate.required[name] = value;
        } else {
            vcstate[name] = value;
        }
        _saveState();
    };
    var _getValue = function (name) {
        return vcstate[name] || (vcstate.required && vcstate.required[name]);
    };
    var _hasValue = function (name) {
        return vcstate[name] !== null && vcstate[name] !== undefined;
    };

    var _enableAllCookies = function () {
        if (window['gtag']) {
            gtag('event', 'enableAllCookies', { method: 'Vc' });
        }
        _setValue('hasSetCookies', 1, true);
        for (var key in DEFAULT_STATE.required) {
            vcstate.required[key] = true;
        }
        _saveState();
    };
    var _disableAllCookies = function () {
        if (window['gtag']) {
            gtag('event', 'disableAllCookies', { method: 'Vc' });
        }
        _setValue('hasSetCookies', 1, true);
        for (var key in DEFAULT_STATE.required) {
            vcstate.required[key] = DEFAULT_STATE.required[key];
        }
        _saveState();
    };
    var _setAllCookies = function (value) {
        if (window['gtag']) {
            gtag('event', 'setAllCookies', { method: 'Vc' });
        }
        _setValue('hasSetCookies', 1, true);
        vcstate.required = value;
        _saveState();
    };
    var _resetAllCookies = function () {
        vcstate = {};
        vcstate.required = JSON.parse(JSON.stringify(DEFAULT_STATE.required));
        _saveState();
    };
    var _getCookieValue = function (name) {
        return vcstate.required && vcstate.required[name];
    };
    var _getAllCookies = function () {
        if (!vcstate.required) {
            vcstate.required = JSON.parse(JSON.stringify(DEFAULT_STATE.required));
        }
        return vcstate.required;
    };

    // Immediate sync restore (available before any component mounts)
    _restoreStateSync();

    // Async native restore (merges native state when ready)
    var _ready = _restoreStateAsync();

    return {
        /** Promise that resolves when native storage has been loaded */
        ready: _ready,
        hasStorage: function () {
            return !!storage || VcStorage.isNative();
        },
        getLocations: function () {
            return vcstate.locations && vcstate.locations.map(function (d) { return d; });
        },
        getCid: function () {
            return vcstate.cid;
        },
        addLocation: _addLocation,
        removeLocation: _removeLocation,
        setUnitGroup: _setUnitGroup,
        getUnitGroup: _getUnitGroup,
        getCookieValue: _getCookieValue,
        enableAllCookies: _enableAllCookies,
        disableAllCookies: _disableAllCookies,
        resetAllCookies: _resetAllCookies,
        getAllCookies: _getAllCookies,
        setAllCookies: _setAllCookies,
        setValue: _setValue,
        getValue: _getValue,
        hasValue: _hasValue,
    };
}());
